import PropTypes from "prop-types";

import {
  ICosignMappedProperty,
  ICosignUnmappedProperty,
} from "manager/hooks/api/integrationQueries";

/** Cosign property statuses returned live from the Cosign API (PRODUCT-988). */
export const COSIGN_PROPERTY_STATUS = Object.freeze({
  active: "active",
  onboarding: "onboarding",
  disabled: "disabled",
});

export const COSIGN_PROPERTY_STATUS_LABELS: Record<string, string> = {
  [COSIGN_PROPERTY_STATUS.active]: "Active",
  [COSIGN_PROPERTY_STATUS.onboarding]: "Onboarding",
  [COSIGN_PROPERTY_STATUS.disabled]: "Disabled",
};

/** CS helper copy for each Cosign property status (PRODUCT-988 AC). */
export const COSIGN_PROPERTY_STATUS_HELPER_COPY: Record<string, string> = {
  [COSIGN_PROPERTY_STATUS.active]:
    "Property is live and can accept Cosign applications.",
  [COSIGN_PROPERTY_STATUS.onboarding]:
    "Waiting on Cosign and the customer to finalise contract details. Reach out to Cosign with the Property UUID (cosign_property_uuid) and PMC ID (property_manager_uuid) if you need help.",
  [COSIGN_PROPERTY_STATUS.disabled]:
    "This property has been disabled on the Cosign side. Contact Cosign support to reactivate.",
  unknown:
    "Cosign status could not be loaded. Reach out to Cosign with the Property UUID and PMC ID if you need help.",
};

/**
 * Formats a dashboard section title with a live property count.
 * When search filters Section 2, shows the visible count vs total (e.g. "2 of 5").
 */
export const formatCosignSectionTitle = (
  title: string,
  visibleCount: number,
  totalCount?: number
): string => {
  if (
    totalCount !== undefined &&
    totalCount !== visibleCount
  ) {
    return `${title} (${visibleCount} of ${totalCount})`;
  }

  return `${title} (${visibleCount})`;
};

/** Row action menu labels from PRODUCT-988 AC. */
export const COSIGN_DASHBOARD_ACTION_LABELS = Object.freeze({
  disconnect: "Disconnect",
  mapToExisting: "Map to existing Cosign property",
  createInCosign: "Create in Cosign",
});

/** Confirmation copy when correcting the company PM UUID (PRODUCT-1134 AC). */
export const COSIGN_PM_UUID_CONFIRMATION_COPY =
  "Changing the Property Manager UUID will affect all property mappings for this company. Are you sure?";

export const COSIGN_PM_UUID_REQUIRED_ERROR = "Property Manager UUID is required.";
export const COSIGN_PM_UUID_INVALID_ERROR =
  "Invalid Property Manager UUID format.";
export const COSIGN_PM_UUID_UNCHANGED_ERROR =
  "This is already the configured Property Manager UUID.";

const COSIGN_PM_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validates a Cosign property manager UUID before save (PRODUCT-1134). */
export const isValidCosignPropertyManagerUuid = (value: string): boolean =>
  COSIGN_PM_UUID_PATTERN.test(value.trim());

/** Returns an inline validation error for a PM UUID draft, or null when valid. */
export const getCosignPmUuidDraftError = (
  draftUuid: string,
  propertyManagerUuid?: string | null
): string | null => {
  const trimmed = draftUuid.trim();
  if (!trimmed) {
    return COSIGN_PM_UUID_REQUIRED_ERROR;
  }
  if (!isValidCosignPropertyManagerUuid(trimmed)) {
    return COSIGN_PM_UUID_INVALID_ERROR;
  }
  if (propertyManagerUuid && trimmed === propertyManagerUuid) {
    return COSIGN_PM_UUID_UNCHANGED_ERROR;
  }
  return null;
};

export interface CosignMappedTableProps {
  properties: ICosignMappedProperty[];
  isLoading: boolean;
  onDisconnect: (property: ICosignMappedProperty) => void;
  onToggle: (property: ICosignMappedProperty, isActive: boolean) => void;
  isToggleUpdating?: boolean;
}

export interface CosignUnmappedTableProps {
  properties: ICosignUnmappedProperty[];
  /** Full unmapped count before client-side search filtering. */
  totalUnmappedCount: number;
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onMapToExisting: (property: ICosignUnmappedProperty) => void;
  onCreateInCosign: (property: ICosignUnmappedProperty) => void;
}

export const cosignMappedTablePropTypes = {
  properties: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDisconnect: PropTypes.func.isRequired,
};

export const cosignUnmappedTablePropTypes = {
  properties: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onMapToExisting: PropTypes.func.isRequired,
  onCreateInCosign: PropTypes.func.isRequired,
};
